import { ReactNode, useState } from 'react';
import dynamic from 'next/dynamic';
import {
  Badge,
  Box,
  Flex,
  FormLabel,
  Grid,
  Select,
  Text,
} from '@chakra-ui/react';
import {
  ArrowRight,
  CaretDown,
  CheckCircle,
  Star,
} from '@phosphor-icons/react';
import { BirchIcon, PineIcon, SpruceIcon } from '../../icons';
import {
  PriceOfWoodFromQuickBi,
  PriceOfWood as PriceOfWoodProps,
  WoodType,
  WoodCuttingType,
  WoodCuttingTypes,
} from '../../types';
import { compactWysiwygStyles } from '../WysiwygStyles';
import { useTranslation } from '../../lib/hooks.context';

const ButtonLink = dynamic(() => import('../ButtonLink'));
const ArrowLink = dynamic(() => import('../ArrowLink'));
const HtmlContent = dynamic(() => import('../HtmlContent'));
const Heading = dynamic(() => import('../Heading'));

type CuttingType =
  | 'Kaikki hakkuut'
  | 'Harvennus'
  | 'Ensiharvennus'
  | 'Uudistushakkuu';

const round = (num) =>
  (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2);

const Header = ({
  title,
  link,
  filterTitle,
  types,
  selectedType,
  setSelectedType,
}: Pick<PriceOfWoodProps, 'title' | 'link'> & {
  filterTitle: PriceOfWoodProps['filter_title'];
  types: WoodCuttingType[];
  selectedType: CuttingType;
  setSelectedType: (type: CuttingType) => void;
}) => {
  const isWoodCuttingType = (value: any): value is WoodCuttingType =>
    (WoodCuttingTypes as readonly string[]).includes(value);

  const t = useTranslation();

  return (
    <Box>
      <Flex
        flexDirection={['column', null, null, 'row']}
        gap="4"
        alignItems={['flex-start', null, null, 'center']}
        justifyContent="space-between"
      >
        <Box>
          {title && (
            <Heading variant="h3" mb="2">
              {title}
            </Heading>
          )}
          {link && (
            <ArrowLink href={link.url} target={link.target}>
              {link.title}
            </ArrowLink>
          )}
        </Box>
        {types && types.length > 0 && (
          <FormLabel
            fontSize="sm"
            display="flex"
            flexDirection="column"
            gap="3"
            m="0"
          >
            {filterTitle}
            <Select
              id="cutting_type"
              maxWidth="600px"
              iconColor="highlightColor"
              icon={<CaretDown size="24" />}
              iconSize="24px"
              size="md"
              bg="white"
              {...(selectedType ? { 'data-filled': true } : null)}
              value={selectedType}
              onChange={(e) => {
                const { value } = e.target;
                if (isWoodCuttingType(value)) {
                  setSelectedType(value);
                } else {
                  console.error('Invalid value selected');
                }
              }}
            >
              {types.map((type) => (
                <option key={type} value={type}>
                  {t(type)}
                </option>
              ))}
            </Select>
          </FormLabel>
        )}
      </Flex>
    </Box>
  );
};

const Promotion = ({
  promotion,
  toAllTitle,
  toMembersTitle,
}: Pick<PriceOfWoodProps, 'promotion'> & {
  toAllTitle: PriceOfWoodProps['to_everyone'];
  toMembersTitle: PriceOfWoodProps['to_members'];
}) => (
  <Box borderTop="1px" borderColor="gray.300" borderStyle="solid" pt="8">
    {promotion?.heading && (
      <Heading variant="h5" mb="6">
        {promotion?.heading}
      </Heading>
    )}
    <Flex flexDirection={['column', null, null, 'row']} gap="5">
      {promotion?.wysiwyg_all && (
        <Flex flex="1" flexDirection="column" gap={2}>
          <Badge
            mr="auto"
            variant="success"
            display="inline-flex"
            gap={2}
            alignItems="center"
          >
            <CheckCircle size="18" />
            {toAllTitle}
          </Badge>
          <HtmlContent
            html={promotion?.wysiwyg_all}
            sx={compactWysiwygStyles}
          />
        </Flex>
      )}
      {promotion?.wysiwyg_members && (
        <Flex flex="1" flexDirection="column" gap={2}>
          <Badge
            mr="auto"
            variant="alert"
            display="inline-flex"
            gap={2}
            alignItems="center"
          >
            <Star size="18" />
            {toMembersTitle}
          </Badge>
          <HtmlContent
            html={promotion?.wysiwyg_members}
            sx={compactWysiwygStyles}
          />
        </Flex>
      )}
      {promotion?.link && (
        <Flex
          flex="1"
          textAlign={['left', null, null, 'right']}
          justifyContent="flex-end"
        >
          <Box alignSelf="flex-end">
            <ButtonLink variant="solidGreen" link={promotion?.link} size="lg" />
          </Box>
        </Flex>
      )}
    </Flex>
  </Box>
);

const InfoBlock = ({
  icon,
  topContent,
  bottomContent,
}: {
  icon: ReactNode;
  topContent: { largeText: string; smallText: WoodType };
  bottomContent: { largeText: string; smallText: WoodType };
}) => {
  const t = useTranslation();

  return (
    <Flex
      flexDirection="column"
      textAlign="center"
      boxShadow="lg"
      borderRadius="lg"
      gap="2"
      bg="white"
      py={[6, null, 10]}
    >
      <Flex pb={8} px={3} flexDirection="column" gap="1">
        <Text fontWeight="bold" textStyle="md">
          {t(topContent.smallText)}
        </Text>
        <Text
          fontSize={['2xl', '3xl', null, '4xl']}
          fontWeight="medium"
          color="greenSpruce"
          whiteSpace="nowrap"
        >
          {topContent.largeText}
          <Text as="span" whiteSpace="nowrap" fontSize="md">
            {' '}
            €/m<sup>3</sup>
          </Text>
        </Text>
      </Flex>
      <Box
        position="relative"
        _before={{
          content: '""',
          display: 'block',
          width: '100%',
          borderBottom: '1px',
          borderStyle: 'solid',
          borderColor: 'gray.300',
          position: 'absolute',
          top: '-1px',
          left: '0',
          right: '0',
        }}
      >
        <Box
          borderRadius="full"
          border="1px"
          borderColor="gray.300"
          borderStyle="solid"
          background="white"
          p={2}
          position="absolute"
          transform="translate(-50%, -50%)"
          left="50%"
        >
          {icon}
        </Box>
      </Box>
      <Box pt={10} px={3}>
        <Text fontWeight="bold" textStyle="md">
          {t(bottomContent.smallText)}
        </Text>
        <Text
          fontSize={['2xl', '3xl', null, '4xl']}
          fontWeight="medium"
          color="greenSpruce"
          whiteSpace="nowrap"
        >
          {bottomContent.largeText}
          <Text as="span" whiteSpace="nowrap" fontSize="md">
            {' '}
            €/m<sup>3</sup>
          </Text>
        </Text>
      </Box>
    </Flex>
  );
};

const WoodPrices = ({
  prices,
}: {
  prices: Record<WoodType, PriceOfWoodFromQuickBi>;
}) => {
  const blocks: [keyof typeof prices, keyof typeof prices][] = [
    ['Mäntytukki', 'Mäntykuitu'],
    ['Kuusitukki', 'Kuusikuitu'],
    ['Koivutukki', 'Koivukuitu'],
  ];

  function getLatest(name: string) {
    return round(prices[name]?.viimeisin_hinta) ?? '';
  }

  return (
    <Grid
      gap="4"
      templateColumns={[
        '1fr',
        'repeat(auto-fit, minmax(400px, 1fr))',
        'repeat(auto-fit, minmax(160px, 1fr))',
        'repeat(auto-fit, minmax(130px, 1fr))',
      ]}
    >
      {blocks.map(([tukki, kuitu]) => (
        <InfoBlock
          key={tukki}
          topContent={{
            largeText: getLatest(tukki),
            smallText: tukki,
          }}
          bottomContent={{
            largeText: getLatest(kuitu),
            smallText: kuitu,
          }}
          icon={
            // eslint-disable-next-line no-nested-ternary
            tukki === 'Mäntytukki' ? (
              <PineIcon size="43" />
            ) : tukki === 'Kuusitukki' ? (
              <SpruceIcon size="43" />
            ) : (
              <BirchIcon size="43" />
            )
          }
        />
      ))}
    </Grid>
  );
};

const WoodPricesSection = ({
  prices,
  text,
}: Pick<PriceOfWoodProps, 'text'> & {
  prices: Record<WoodType, PriceOfWoodFromQuickBi>;
}) => (
  <Flex flexDirection="column" as="section" gap="8" height="100%">
    <WoodPrices prices={prices} />
    <HtmlContent html={text} sx={compactWysiwygStyles} />
  </Flex>
);

const WoodPriceChanges = ({
  prices,
  selectedType,
}: {
  prices: PriceOfWoodFromQuickBi[];
  selectedType: CuttingType;
}) => {
  const t = useTranslation();
  const Row = ({
    title,
    lastPrice,
    previousPrice,
    change,
    isNegative,
  }: {
    title: WoodType;
    lastPrice: string;
    previousPrice: string;
    change: string;
    isNegative: boolean;
  }) => (
    <Flex
      width="100%"
      justifyContent={['space-between', 'space-around']}
      alignItems="center"
      py="4"
      borderBottom="2px solid"
      borderColor="gray2"
      _last={{ borderBottom: 'none' }}
    >
      <Flex flexDirection="column" gap="1">
        <Text textStyle="sm" display="flex" gap="1">
          <Text as="span" fontWeight="600">
            {t(title)}
          </Text>
          <Text as="span" textStyle="xs" opacity="0.1">
            |
          </Text>
          <Text as="span" color="black" opacity="0.4" fontWeight="600">
            {t(selectedType)}
          </Text>
        </Text>
        <Flex gap="2" alignItems="center">
          <Text
            as="span"
            fontSize={['lg', 'xl', '2xl', '3xl']}
            color="black"
            opacity="0.3"
            fontWeight="600"
          >
            {previousPrice}
          </Text>
          <ArrowRight size={20} />
          <Text
            as="span"
            fontSize={['lg', 'xl', '2xl', '3xl']}
            fontWeight="600"
          >
            {lastPrice}
          </Text>
          <Text
            as="span"
            color="gray5"
            fontWeight="600"
            alignSelf="flex-end"
            textStyle="sm"
            lineHeight={['1.8', null, null, '2.2']}
          >
            €/m3
          </Text>
        </Flex>
      </Flex>
      <Text
        borderRadius="full"
        py="1"
        px="3"
        bg={isNegative ? 'redLight' : 'greenLight'}
        color={isNegative ? 'redError' : 'greenSpruce'}
        textStyle="lg"
        fontWeight="600"
        minWidth={['null', null, null, '100px']}
        textAlign="center"
      >
        {change}
      </Text>
    </Flex>
  );
  return (
    <Flex flexWrap="wrap" columnGap="4" justifyContent="center">
      {prices &&
        prices
          .slice(0, 3)
          .map(
            ({
              puutavaralaji,
              viimeisin_hinta,
              edeltava_hinta,
              muutos_suhteellinen,
            }) => (
              <Row
                key={puutavaralaji}
                title={puutavaralaji}
                lastPrice={round(viimeisin_hinta)}
                previousPrice={round(edeltava_hinta)}
                change={`${round(muutos_suhteellinen * 100)}%`}
                isNegative={muutos_suhteellinen < 0}
              />
            ),
          )}
    </Flex>
  );
};

const WoodPriceChangeSection = ({
  title,
  prices,
  selectedType,
}: {
  title: PriceOfWoodProps['subtitle'];
  prices: PriceOfWoodFromQuickBi[];
  selectedType: CuttingType;
}) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    bg="white"
    as="section"
    borderRadius="lg"
    gap="4"
    pt={6}
    pb={8}
    px={[4, null, null, 8]}
    boxShadow="lg"
  >
    {title && <Heading variant="h5">{title}</Heading>}
    <WoodPriceChanges selectedType={selectedType} prices={prices} />
  </Flex>
);

const PriceOfWood = ({
  prices,
  title,
  subtitle,
  text,
  link,
  filter_title: filterTitle,
  to_everyone: toAllTitle,
  to_members: toMembersTitle,
  promotion,
}: PriceOfWoodProps) => {
  const types: WoodCuttingType[] = [
    'Uudistushakkuu',
    'Ensiharvennus',
    'Harvennus',
    'Kaikki hakkuut',
  ];

  const [selectedType, setSelectedType] = useState(types[0]);

  const selectedCuttingTypePrices = prices.filter((item) => {
    const { hakkuutapa } = item;
    return hakkuutapa === selectedType;
  });

  const groupedByWoodType = selectedCuttingTypePrices.reduce((acc, item) => {
    const { puutavaralaji } = item;

    acc[puutavaralaji] = item; // eslint-disable-line no-param-reassign

    return acc;
  }, {} as Record<WoodType, PriceOfWoodFromQuickBi>);

  const arrangedByLargestPriceChange = selectedCuttingTypePrices.sort(
    (a, b) => Math.abs(b.muutos_suhteellinen) - Math.abs(a.muutos_suhteellinen),
  );

  return (
    <Grid
      gridTemplateColumns="1.3fr 1fr"
      gridColumnGap="8"
      gap="8"
      bg="gray1"
      gridAutoFlow="column"
      borderRadius="lg"
      px={[4, 8, null, 12]}
      py={[6, null, null, 12]}
    >
      <Box gridColumn="1/3">
        <Header
          title={title}
          filterTitle={filterTitle}
          link={link}
          types={types}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
        />
      </Box>
      <Box gridColumn={['1/3', null, null, null, '1/2']}>
        <WoodPricesSection prices={groupedByWoodType} text={text} />
      </Box>
      <Box gridColumn={['1/3', null, null, null, '2/3']}>
        <WoodPriceChangeSection
          title={subtitle}
          selectedType={selectedType}
          prices={arrangedByLargestPriceChange}
        />
      </Box>
      <Box gridColumn="1/3">
        <Promotion
          promotion={promotion}
          toAllTitle={toAllTitle}
          toMembersTitle={toMembersTitle}
        />
      </Box>
    </Grid>
  );
};

export default PriceOfWood;
